import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCountryList } from '@queries/countries/useCountryList';
import { findKeysOfNullValues } from '@utils/common';
import {
  CreateOrderRequest,
  CustomerDetail,
  createOrder,
} from '../../apis/order';
import { useRecoilValue } from 'recoil';
import { userState } from '../../recoils/atoms';
import { RequiredDataType } from '../../models/product';
import { useActivePartnerProductPrices } from '../../queries/partners/useActivePartnerProductPrices';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type ProductOption = {
  label: string;
  value: string;
};

const normalizeOptionText = (value: unknown) =>
  String(value ?? '').trim().toLowerCase();

const getField = (field: RequiredDataType) => {
  switch (field) {
    case 'ACTIVATION_DATE':
      return 'activationDate';
    case 'VALIDITY_DAYS':
      return 'validDays';
    case 'IMEI':
      return 'imei';
    case 'EID':
      return 'eid';
    case 'CUSTOMER_NAME':
      return 'customerName';
  }
  return '';
};

const getValue = (
  customerDetail: CustomerDetail,
  field: RequiredDataType
): string => {
  switch (field) {
    case 'ACTIVATION_DATE':
      return customerDetail.activationDate ?? '';
    case 'VALIDITY_DAYS':
      return customerDetail.validDays?.toString() ?? '';
    case 'IMEI':
      return customerDetail.imei ?? '';
    case 'EID':
      return customerDetail.eid ?? '';
    case 'CUSTOMER_NAME':
      return customerDetail.customerName ?? '';
  }
  return '';
};
export const AddOrder = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();
  const didRedirectUnauthorizedRef = useRef(false);

  const [form, setForm] = useState<CreateOrderRequest>({
    productCode: '',
    quantity: 0,
    customerDetails: [],
    sendEmail: true,
  });
  const [productInputValue, setProductInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useRecoilValue(userState);

  useEffect(() => {
    if (!user) return;
    if (user.manualOrderEnabled) return;
    if (didRedirectUnauthorizedRef.current) return;

    didRedirectUnauthorizedRef.current = true;
    systemAlert.warning('Manual order is not enabled for this account.');
    navigate('/orders', { replace: true });
  }, [navigate, systemAlert, user]);

  const { data: productPrices } = useActivePartnerProductPrices({
    page: 1,
    pageSize: 10000,
    partnerId: user?.partnerId ?? 0,
    searchParam: {},
  });
  const requiredFields: RequiredDataType[] = useMemo(
    () =>
      (productPrices?.data ?? [])
        .filter((p) => p.productCode === form.productCode)
        .map((p) => p.requiredData.requiredFields),
    [productPrices, form.productCode]
  ).flatMap((r) => r);

  const productOptions = useMemo<ProductOption[]>(
    () =>
      (productPrices?.data ?? []).map((p) => ({
        label: p.productName,
        value: p.productCode,
      })),
    [productPrices?.data]
  );
  const selectedProductOption = useMemo(
    () => productOptions.find((p) => p.value === form.productCode) || null,
    [form.productCode, productOptions]
  );
  const filteredProductOptions = useMemo(() => {
    const keyword = normalizeOptionText(productInputValue);
    if (!keyword) return productOptions;

    return productOptions.filter((option) =>
      normalizeOptionText(option.label).includes(keyword)
    );
  }, [productInputValue, productOptions]);
  const productListboxKey = normalizeOptionText(productInputValue) || 'all';
  const productListboxProps = useMemo(
    () => ({ key: productListboxKey, role: 'listbox' as const }),
    [productListboxKey]
  );

  useEffect(() => {
    if (selectedProductOption) {
      setProductInputValue(selectedProductOption.label);
    }
  }, [form.productCode, selectedProductOption]);

  const { data: countriesList } = useCountryList();

  const countryOptions = useMemo(
    () =>
      (countriesList?.data ?? []).map((c) => ({
        label: c.englishName,
        value: c.countryCode,
      })),
    [countriesList]
  );

  const handleChangeInput = (
    name: keyof CreateOrderRequest,
    value: string | boolean | number | null
  ) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleChangeQuantity = (quantity: number) => {
    if (form.customerDetails.length < quantity) {
      setForm({
        ...form,
        quantity: quantity,
        customerDetails: [
          ...form.customerDetails,
          ...Array.from(
            { length: quantity - form.customerDetails.length },
            () => ({})
          ),
        ],
      });
    } else {
      setForm({
        ...form,
        quantity: quantity,
        customerDetails: form.customerDetails.slice(0, quantity),
      });
    }
  };

  const handleUpdateCustomerDetail = (
    index: number,
    customerDetail: CustomerDetail
  ) => {
    setForm((prev) => ({
      ...prev,
      customerDetails: prev.customerDetails.map((d, i) =>
        i === index ? customerDetail : d
      ),
    }));
  };
  const handleCancel = async () => {
    const ok = await confirm({
      priority: 'warning',
      title: 'Cancel order creation?',
      description: 'Entered order information will be discarded.',
      confirmText: 'Cancel',
      cancelText: 'Keep editing',
      autoFocusConfirm: false,
    });

    if (ok) {
      navigate(-1);
    }
  };

  const handleAdd = async () => {
    if (isSubmitting) return;

    const missingKeys = findKeysOfNullValues(form);
    if (missingKeys.length > 0) {
      systemAlert.error(
        `The following fields are missing or invalid: ${missingKeys.join(', ')}`
      );
      return;
    }

    const customerDetails = form.customerDetails.map((d) => {
      const updated: CustomerDetail = {
        customerName: d.customerName,
        phoneNumber: d.phoneNumber,
        email: d.email,
      };
      if (requiredFields.includes(RequiredDataType.ACTIVATION_DATE)) {
        updated.activationDate = d.activationDate;
      }
      if (requiredFields.includes(RequiredDataType.VALIDITY_DAYS)) {
        updated.validDays = d.validDays;
      }
      if (requiredFields.includes(RequiredDataType.IMEI)) {
        updated.imei = d.imei;
      }
      if (requiredFields.includes(RequiredDataType.EID)) {
        updated.eid = d.eid;
      }
      if (requiredFields.includes(RequiredDataType.CUSTOMER_NAME)) {
        updated.customerName = d.customerName;
      }
      if (requiredFields.includes(RequiredDataType.PASSPORT_DATA)) {
        updated.passportData = d.passportData;
      }
      return updated;
    });

    setIsSubmitting(true);

    try {
      const response = await createOrder({
        ...form,
        customerDetails: customerDetails,
      });

      if (response.code === 2000) {
        systemAlert.success('Order added successfully.');
        navigate('/orders');
      } else {
        systemAlert.error(response.message || 'Failed to add order.');
      }
    } catch {
      systemAlert.error('Failed to add order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (user && !user.manualOrderEnabled) {
    return null;
  }

  return (
    <>
      <Stack spacing={3} mb={4} mt={4}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Autocomplete
            disablePortal
            options={filteredProductOptions}
            value={selectedProductOption}
            inputValue={productInputValue}
            onInputChange={(_, value, reason) => {
              setProductInputValue(value);

              if (
                reason === 'input' &&
                form.productCode &&
                value !== selectedProductOption?.label
              ) {
                handleChangeInput('productCode', null);
              }
            }}
            onChange={(_, value) => {
              handleChangeInput('productCode', value?.value || null);
              setProductInputValue(value?.label ?? '');
            }}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            ListboxProps={productListboxProps}
            renderInput={(params) => (
              <TextField {...params} label="Product" placeholder="Select Product" size="small" />
            )}
            sx={{ minWidth: 280, maxWidth: 380, flex: 1 }}
          />

          <TextField
            label="Quantity"
            value={form.quantity}
            onChange={(e) => handleChangeQuantity(Number(e.target.value))}
            size="small"
            type="number"
            inputProps={{ min: 0 }}
            sx={{ width: { xs: '100%', md: 180 } }}
          />
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={form.sendEmail}
              onChange={(_, checked) => handleChangeInput('sendEmail', checked)}
            />
          }
          label="Send Email to Customers"
        />

        {form.customerDetails.length > 0 && (
          <>
            <Divider />
            <Stack spacing={3}>
              {form.customerDetails.map((detail, index) => (
                <Stack key={`customer-${index}`} spacing={2}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    Customer #{index + 1}
                  </Typography>

                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
                    alignItems="center"
                    gap={2}
                  >
                    <TextField
                      label="Email"
                      value={detail.email ?? ''}
                      size="small"
                      onChange={(e) =>
                        handleUpdateCustomerDetail(index, {
                          ...detail,
                          email: e.target.value,
                        })
                      }
                    />
                    <TextField
                      label="Phone Number"
                      value={detail.phoneNumber ?? ''}
                      size="small"
                      onChange={(e) =>
                        handleUpdateCustomerDetail(index, {
                          ...detail,
                          phoneNumber: e.target.value,
                        })
                      }
                    />
                    <TextField
                      label={RequiredDataType.CUSTOMER_NAME}
                      value={detail.customerName ?? ''}
                      size="small"
                      onChange={(e) =>
                        handleUpdateCustomerDetail(index, {
                          ...detail,
                          customerName: e.target.value,
                        })
                      }
                    />
                    {requiredFields
                      .filter((field) => field !== RequiredDataType.PASSPORT_DATA)
                      .map((field) => (
                        <TextField
                          key={`customer-${index}-${field}`}
                          label={field}
                          value={getValue(detail, field)}
                          size="small"
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              customerDetails: prev.customerDetails.map((d, i) =>
                                i === index
                                  ? {
                                      ...d,
                                      [getField(field)]: e.target.value,
                                    }
                                  : d
                              ),
                            }))
                          }
                          type={
                            field === RequiredDataType.ACTIVATION_DATE
                              ? 'date'
                              : field === RequiredDataType.VALIDITY_DAYS
                              ? 'number'
                              : 'text'
                          }
                          InputLabelProps={
                            field === RequiredDataType.ACTIVATION_DATE ? { shrink: true } : undefined
                          }
                        />
                      ))}
                  </Box>

                  {requiredFields.includes(RequiredDataType.PASSPORT_DATA) && (
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" fontWeight={800}>
                        Customer #{index + 1} Passport Info
                      </Typography>

                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
                        alignItems="center"
                        gap={2}
                      >
                        <TextField
                          label="Passport Number"
                          value={detail.passportData?.passportNumber ?? ''}
                          size="small"
                          onChange={(e) =>
                            handleUpdateCustomerDetail(index, {
                              ...detail,
                              passportData: {
                                ...detail.passportData,
                                passportNumber: e.target.value,
                              },
                            })
                          }
                        />

                        <TextField
                          label="Name"
                          value={detail.passportData?.passportName ?? ''}
                          size="small"
                          onChange={(e) =>
                            handleUpdateCustomerDetail(index, {
                              ...detail,
                              passportData: {
                                ...detail.passportData,
                                passportName: e.target.value,
                              },
                            })
                          }
                        />

                        <TextField
                          label="BirthDate"
                          value={detail.passportData?.passportBirthdate ?? ''}
                          size="small"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) =>
                            handleUpdateCustomerDetail(index, {
                              ...detail,
                              passportData: {
                                ...detail.passportData,
                                passportBirthdate: e.target.value,
                              },
                            })
                          }
                        />

                        <Autocomplete
                          disablePortal
                          options={countryOptions}
                          value={
                            countryOptions.find(
                              (c) => c.value === (detail.passportData?.passportCountry ?? 'US')
                            ) || null
                          }
                          onChange={(_, value) =>
                            handleUpdateCustomerDetail(index, {
                              ...detail,
                              passportData: {
                                ...detail.passportData,
                                passportCountry: value?.value ? String(value.value) : 'US',
                              },
                            })
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Country" placeholder="Select Country" size="small" />
                          )}
                        />
                      </Box>
                    </Stack>
                  )}
                </Stack>
              ))}
            </Stack>
          </>
        )}
        <Divider />
      </Stack>
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'flex-end'}
        gap={2}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </Box>
    </>
  );
};
